if (!customElements.get('collection-card-swatches')) {
  class CollectionCardSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('[data-card-swatches]') || this;
      this.swatches = [...this.card.querySelectorAll('[data-card-swatch]')];

      this.linkTargets = [...this.card.querySelectorAll('[data-card-link]')];
      this.primaryImage = this.card.querySelector('[data-card-primary-image]');
      this.secondaryImage = this.card.querySelector('[data-card-secondary-image]');
      this.mediaCarousel = this.card.querySelector('[data-card-media-carousel]');
      this.secondaryEnabled = this.mediaCarousel?.dataset?.secondaryEnabled !== 'false';
      this.mediaStatus = this.card.querySelector('[data-card-media-status]');
      this.price = this.card.querySelector('[data-card-price]');
      this.variantInput = this.card.querySelector('[data-card-variant-input]');
      this.addButton = this.card.querySelector('[data-card-add-button]');

      this.bindMediaCarousel();
      this.secondaryImage?.addEventListener('error', () => this.collapseInstalledMedia());

      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', (event) => {
          event.preventDefault();
          this.applySwatch(swatch);
        });
      });
    }

    collapseInstalledMedia() {
      if (this.secondaryImage) this.secondaryImage.hidden = true;
      this.mediaCarousel?.classList.remove('has-installed');
      this.mediaCarousel?.scrollTo({ left: 0, behavior: 'auto' });
      this.mediaStatus?.classList.add('is-hidden');
      this.updateMediaStatus();
    }

    bindMediaCarousel() {
      if (!this.mediaCarousel) return;

      let pointerStartX = 0;
      let pointerStartY = 0;
      let pointerStartScroll = 0;
      let dragged = false;
      let isPointerDown = false;
      let tapCandidate = false;

      this.mediaCarousel.addEventListener('pointerdown', (event) => {
        isPointerDown = true;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        pointerStartScroll = this.mediaCarousel.scrollLeft;
        dragged = false;
        tapCandidate = false;
      });
      this.mediaCarousel.addEventListener('pointermove', (event) => {
        if (!isPointerDown) return;
        if (
          Math.abs(event.clientX - pointerStartX) > 8 ||
          Math.abs(event.clientY - pointerStartY) > 8 ||
          Math.abs(this.mediaCarousel.scrollLeft - pointerStartScroll) > 8
        ) {
          dragged = true;
        }
      });
      this.mediaCarousel.addEventListener('pointerup', (event) => {
        tapCandidate = !dragged && ['touch', 'pen'].includes(event.pointerType);
        isPointerDown = false;
      });
      this.mediaCarousel.addEventListener('pointercancel', () => {
        isPointerDown = false;
        tapCandidate = false;
      });
      this.mediaCarousel.addEventListener('lostpointercapture', () => {
        isPointerDown = false;
      });
      this.mediaCarousel.addEventListener('click', (event) => {
        if (dragged) {
          event.preventDefault();
          dragged = false;
          tapCandidate = false;
          return;
        }

        if (
          !tapCandidate ||
          !window.matchMedia('(max-width: 989px)').matches ||
          !this.mediaCarousel.classList.contains('has-installed')
        ) return;
        tapCandidate = false;

        const installedIsActive = this.mediaCarousel.scrollLeft >= this.mediaCarousel.clientWidth * 0.5;
        if (installedIsActive) return;

        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mediaCarousel.scrollTo({
          left: this.mediaCarousel.clientWidth,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      });
      this.mediaCarousel.addEventListener('scroll', () => window.requestAnimationFrame(() => this.updateMediaStatus()), {
        passive: true,
      });
    }

    updateMediaStatus() {
      if (!this.mediaCarousel || !this.mediaStatus) return;
      const installedIsActive = this.mediaCarousel.classList.contains('has-installed') &&
        this.mediaCarousel.scrollLeft >= this.mediaCarousel.clientWidth * 0.5;
      this.mediaStatus.querySelectorAll('span').forEach((dot, index) => {
        dot.classList.toggle('is-active', installedIsActive ? index === 1 : index === 0);
      });
    }

    applySwatch(swatch) {
      this.swatches.forEach((item) => {
        const active = item === swatch;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      const variantUrl = swatch.dataset.variantUrl;
      if (variantUrl) {
        this.linkTargets.forEach((link) => {
          link.href = variantUrl;
        });
        this.card.querySelectorAll('[data-product-url]').forEach((target) => {
          target.dataset.productUrl = variantUrl;
        });
      }

      if (this.primaryImage) {
        if (swatch.dataset.mediaSrc) this.primaryImage.src = swatch.dataset.mediaSrc;
        if (swatch.dataset.mediaSrcset) this.primaryImage.srcset = swatch.dataset.mediaSrcset;
        if (swatch.dataset.mediaAlt) this.primaryImage.alt = swatch.dataset.mediaAlt;
        if (swatch.dataset.mediaWidth) this.primaryImage.width = Number(swatch.dataset.mediaWidth);
        if (swatch.dataset.mediaHeight) this.primaryImage.height = Number(swatch.dataset.mediaHeight);
      }

      const installedConfigured = this.secondaryEnabled !== false && Boolean(this.secondaryImage) && swatch.dataset.installedConfigured === 'true';
      const keepInstalledSlide = installedConfigured && this.mediaCarousel &&
        this.mediaCarousel.scrollLeft >= this.mediaCarousel.clientWidth * 0.5;

      if (this.secondaryImage) {
        this.secondaryImage.hidden = !installedConfigured;
        if (installedConfigured) {
          if (swatch.dataset.installedSrc) this.secondaryImage.src = swatch.dataset.installedSrc;
          if (swatch.dataset.installedSrcset) this.secondaryImage.srcset = swatch.dataset.installedSrcset;
          this.secondaryImage.alt = swatch.dataset.installedAlt || `${this.secondaryImage.closest('.card-wrapper')?.querySelector('.card__heading')?.textContent?.trim() || 'Product'} installed in the selected finish`;
          if (swatch.dataset.installedWidth) this.secondaryImage.width = Number(swatch.dataset.installedWidth);
          if (swatch.dataset.installedHeight) this.secondaryImage.height = Number(swatch.dataset.installedHeight);
        }
      }

      if (this.mediaCarousel) {
        this.mediaCarousel.classList.toggle('has-installed', installedConfigured);
        window.requestAnimationFrame(() => {
          this.mediaCarousel.scrollTo({
            left: keepInstalledSlide ? this.mediaCarousel.clientWidth : 0,
            behavior: 'auto',
          });
          this.updateMediaStatus();
        });
      }
      if (this.mediaStatus) this.mediaStatus.classList.toggle('is-hidden', !installedConfigured);

      if (this.price && swatch.dataset.priceHtml) {
        this.price.innerHTML = swatch.dataset.priceHtml;
      }

      if (this.variantInput && swatch.dataset.variantId) {
        this.variantInput.value = swatch.dataset.variantId;
        if (swatch.dataset.available === 'true') this.variantInput.removeAttribute('disabled');
        else this.variantInput.setAttribute('disabled', 'disabled');
      }

      if (this.addButton) {
        const available = swatch.dataset.available === 'true';
        this.addButton.disabled = !available;
        const label = this.addButton.querySelector('span');
        if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
      }
    }
  }

  customElements.define('collection-card-swatches', CollectionCardSwatches);
}

document.querySelectorAll('[data-card-swatches]').forEach((card) => {
  if (card.dataset.cardSwatchesBound === 'true') return;
  card.dataset.cardSwatchesBound = 'true';
  const controller = document.createElement('collection-card-swatches');
  card.appendChild(controller);
});
